import { useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, waLink } from "@/lib/site";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  useEffect(() => {}, []);
  return (
    <section
      id="utama"
      data-testid="hero-section"
      className="relative bg-white pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-70" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <motion.span
              variants={fade}
              initial="hidden"
              animate="show"
              custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm font-medium text-[#0A2540]"
            >
              <ShieldCheck className="h-4 w-4 text-[#F59E0B]" />
              Dipercayai • Selamat • Sulit
            </motion.span>

            <motion.h1
              variants={fade}
              initial="hidden"
              animate="show"
              custom={1}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0A2540] leading-[1.05]"
            >
              Urusan Pinjaman Perumahan{" "}
              <span className="text-[#1D4ED8]">Mudah, Selamat &amp; Pantas</span>
            </motion.h1>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed"
            >
              Saya <strong className="text-[#0A2540]">{SITE.agentName}</strong> dari{" "}
              <strong className="text-[#0A2540]">{SITE.agency}</strong> membantu anda
              memohon pinjaman rumah dengan bimbingan penuh — dari semakan kelayakan
              sehingga serahan kunci. Dokumen anda dilindungi &amp; dirahsiakan sepenuhnya.
            </motion.p>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <a href="#mohon" data-testid="hero-apply-btn">
                <Button className="w-full sm:w-auto h-12 px-7 text-base bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full transition-transform hover:scale-105">
                  Semak Kelayakan Percuma
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
              >
                <Button
                  variant="outline"
                  className="w-full sm:w-auto h-12 px-7 text-base border-slate-300 text-[#0A2540] hover:bg-slate-50 rounded-full transition-transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 h-5 w-5 text-[#10B981]" />
                  WhatsApp Saya
                </Button>
              </a>
            </motion.div>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 flex items-center gap-8"
            >
              <div>
                <p className="text-3xl font-extrabold text-[#0A2540]">100%</p>
                <p className="text-sm text-slate-500">Data Sulit</p>
              </div>
              <div className="h-10 w-px bg-slate-200" />
              <div>
                <p className="text-3xl font-extrabold text-[#0A2540]">Percuma</p>
                <p className="text-sm text-slate-500">Semakan Awal</p>
              </div>
              <div className="h-10 w-px bg-slate-200" />
              <div>
                <p className="text-3xl font-extrabold text-[#0A2540]">Bank</p>
                <p className="text-sm text-slate-500">Utama Malaysia</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="md:col-span-5 relative"
          >
            <div className="relative rounded-3xl bg-gradient-to-b from-slate-50 to-white border border-slate-100 p-2">
              <img
                src={SITE.photo}
                alt={`${SITE.agentName} - Perunding Hartanah dausREALTY`}
                className="w-full h-auto object-contain rounded-2xl bg-white"
                loading="eager"
                data-testid="hero-agent-photo"
              />
              <div className="absolute bottom-5 left-5 rounded-2xl bg-white/90 backdrop-blur-md border border-slate-100 px-5 py-3 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <p className="font-heading font-bold text-[#0A2540] leading-tight">
                  {SITE.agentName}
                </p>
                <p className="text-sm text-slate-500">{SITE.agency}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
